import React, {FC, PropsWithChildren, useRef} from 'react';
import {useDrop} from 'react-dnd';
import {NativeTypes} from 'react-dnd-html5-backend';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {FrontDragEvent, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {center, ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {makeSizeConstants} from '../../../../libs/shared/ui/src/styles/styleHelpers';
import {logger} from '../../../../srcCommon/services/logger/logger';
import {parseDragAndDropJson} from '../../../helpers/dragAndDropHelpers';
import {isUploadedAttachmentModel} from '../../../models/api/attachmentModel';
import {AttachmentDragType} from '../../inboxes/viewer/activity/attachments/attachmentDragSource';
import {
  DraggedAttachmentItemsModel,
  DraggedAttachmentItemTypesEnum,
  RDraggedAttachmentItems,
} from './attachmentsDragAndDrop';

/*
 * Props.
 */

export type AttachmentFileDropHandler = (file: File, shouldPreventInline?: boolean) => void;

interface AttachmentsDropzoneProps {
  className?: string;
  size?: VisualSizesEnum;
  onDropAttachmentItems: HandlerOf<DraggedAttachmentItemsModel>;
  onDropFile: AttachmentFileDropHandler;
  shouldScroll?: boolean;
}

/*
 * Style.
 */

const StyledZoneDiv = styled.div`
  position: relative;
`;

const StyledChildrenDiv = styled.div<{$shouldScroll?: boolean}>`
  flex: 1;
  display: flex;
  flex-direction: column;
  ${(p) =>
    p.$shouldScroll &&
    css`
      overflow: auto;
    `};
`;

const StyledOverlayDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  background-color: ${(p) => p.theme.alphas.white50};
  ${center()};
`;

const overlayFontSizes = makeSizeConstants(fontSizes.medium, fontSizes.large);

interface TextDivStyleProps {
  $size: VisualSizesEnum;
}
const StyledTextDiv = styled.div<TextDivStyleProps>`
  text-align: center;
  color: ${(p) => p.theme.greys.shade50};
  font-size: ${(p) => overlayFontSizes[p.$size]};
  font-weight: ${fontWeights.semibold};
  ${ellipsis()};
`;

/*
 * DnD.
 */

interface FileDragItem {
  files?: ReadonlyArray<File>;
}

interface FrontNativeDragItem {
  text?: string;
}

function handleDrop(props: AttachmentsDropzoneProps, type: unknown, item: unknown, isAltPressed?: boolean) {
  switch (type) {
    // Attachment from the same window.
    case AttachmentDragType: {
      if (isUploadedAttachmentModel(item)) {
        props.onDropAttachmentItems({
          type: DraggedAttachmentItemTypesEnum.UPLOADED_ATTACHMENTS,
          attachments: [item],
        });
      }
      break;
    }
    // Native file.
    case NativeTypes.FILE: {
      const {files = []} = item as FileDragItem;
      files.forEach((file) => {
        // Workaround for https://front.atlassian.net/browse/PB-29204 and other bad Chrome DownloadURL payloads.
        if (file.size === 0) {
          logger.warn('Attempt to drop an empty file attachment', {
            file_type: file.type,
          });
          return;
        }

        props.onDropFile(file, isAltPressed);
      });
      break;
    }
    // Native front attachment, potentially an attachment from another window.
    case NativeTypes.FRONT_ATTACHMENT: {
      // Note: these attachments are not available in canDrop.
      const {text} = item as FrontNativeDragItem;
      const draggedItems = parseDragAndDropJson(RDraggedAttachmentItems, text);
      if (!draggedItems) {
        return;
      }

      props.onDropAttachmentItems(draggedItems);
      break;
    }
    default:
  }
}

/*
 * Component.
 */

export const AttachmentsDropzone: FC<PropsWithChildren<AttachmentsDropzoneProps>> = (props) => {
  const {className, children} = props;
  const hasAltRef = useRef(false);
  const [isOver, dropRef] = useDrop({
    accept: [AttachmentDragType, NativeTypes.FRONT_ATTACHMENT, NativeTypes.FILE],
    drop: (item, monitor) => handleDrop(props, monitor.getItemType(), item, hasAltRef.current),
    collect: (monitor) => monitor.isOver(),
  });

  const onDropCapture = (e: FrontDragEvent) => {
    hasAltRef.current = e.altKey;
  };

  return (
    <StyledZoneDiv className={className} ref={dropRef} onDropCapture={onDropCapture}>
      <StyledChildrenDiv $shouldScroll={props.shouldScroll}>{children}</StyledChildrenDiv>
      {maybeRenderOverlay(props, isOver)}
    </StyledZoneDiv>
  );
};

function maybeRenderOverlay(props: AttachmentsDropzoneProps, isOver: boolean) {
  if (!isOver) {
    return null;
  }

  return (
    <StyledOverlayDiv>
      <StyledTextDiv $size={props.size || VisualSizesEnum.MEDIUM}>
        <Trans id="attachments-overlay">Drop to attach files</Trans>
      </StyledTextDiv>
    </StyledOverlayDiv>
  );
}
